import axios from 'axios';

// Backend URL for connecting to server
// For production with reverse proxy, use /api path
// For local development, backend also serves under /api on port 5001
const BACKEND_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? window.location.protocol + '//' + window.location.hostname + ':5001/api'
    : window.location.protocol + '//' + window.location.hostname + '/api';


export const apiClient = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes in milliseconds
  withCredentials: true, // Enable cookies for session management
  // Add additional error handling
  validateStatus: function (status) {
    return status >= 200 && status < 600; // Resolve only if status is within this range
  }
});

// Authentication APIs
export const register = async (userData) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/auth/register', userData);
    console.log('Register response:', response);
    return response.data;
  } catch (error) {
    console.error('Error registering user:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Registration failed'
    };
  }
};

export const login = async (credentials) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/auth/login', credentials);
    console.log('Login response:', response);
    return response.data;
  } catch (error) {
    console.error('Error logging in:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Login failed'
    };
  }
};

export const loginWithMicrosoft = async (idToken) => {
  try {
    const response = await apiClient.post('/auth/entra-login', { id_token: idToken });
    return response.data;
  } catch (error) {
    console.error('Error logging in with Microsoft:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Microsoft login failed'
    };
  }
};

export const getMicrosoftSsoConfig = async () => {
  try {
    const response = await apiClient.get('/auth/entra-config');
    return response.data;
  } catch (error) {
    console.error('Error loading Microsoft SSO configuration:', error);
    return {
      success: false,
      enabled: false,
      message: error.response?.data?.message || error.message || 'Microsoft SSO configuration is unavailable'
    };
  }
};

export const logout = async () => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/auth/logout');
    return response.data;
  } catch (error) {
    console.error('Error logging out:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Logout failed'
    };
  }
};

export const getCurrentUser = async () => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.get('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error getting current user:', error);
    return {
      success: false,
      user: null
    };
  }
};

/**
 * Get all users (admin only)
 */
export const getUsers = async () => {
  try {
    const response = await apiClient.get('/auth/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    return { success: false, message: 'Failed to fetch users' };
  }
};

/**
 * Get users assigned to a project
 * @param {number} projectId - The project ID
 */
export const getProjectUsers = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/users`);
    console.log(`Project users response:`, response.data);
    return response.data;
  } catch (error) {
    console.error(`Error fetching users for project ${projectId}:`, error);
    // Return a proper structure to prevent errors
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to fetch project users',
      users: []
    };
  }
};

/**
 * Create a new user (admin only)
 * @param {Object} userData - User data (email, full_name, password, role)
 */
export const createUser = async (userData) => {
  try {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, message: 'Failed to create user' };
  }
};

/**
 * Update an existing user (admin only)
 * @param {number} userId - User ID
 * @param {Object} userData - User data to update
 */
export const updateUser = async (userId, userData) => {
  try {
    const response = await apiClient.put(`/auth/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    return { success: false, message: 'Failed to update user' };
  }
};

/**
 * Delete a user (admin only)
 * @param {number} userId - User ID
 */
export const deleteUser = async (userId) => {
  try {
    const response = await apiClient.delete(`/auth/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    return { success: false, message: 'Failed to delete user' };
  }
};

/**
 * Assign a user to a project (admin only)
 * @param {number} projectId - Project ID
 * @param {Object} assignmentData - { user_id: number, role: string }
 */
export const assignUserToProject = async (projectId, assignmentData) => {
  try {
    // Use the apiClient instead of fetch for consistency with other API calls
    const response = await apiClient.post(`/projects/${projectId}/users`, assignmentData);
    return response.data;
  } catch (error) {
    console.error('Error assigning user to project:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to assign user to project' 
    };
  }
};

/**
 * Remove a user from a project (admin only)
 * @param {number} projectId - Project ID
 * @param {number} userId - User ID
 */
export const removeUserFromProject = async (projectId, userId) => {
  try {
    // Also update this function to use apiClient
    const response = await apiClient.delete(`/projects/${projectId}/users/${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error removing user from project:', error);
    return { 
      success: false, 
      message: error.response?.data?.message || 'Failed to remove user from project' 
    };
  }
};

/**
 * Get all projects assigned to a user
 * @param {number} userId - User ID
 */
export const getUserProjects = async (userId) => {
  try {
    const response = await apiClient.get(`/users/${userId}/projects`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user projects:', error);
    return { success: false, message: 'Failed to fetch user projects' };
  }
};

// Project management APIs
export const getProjects = async () => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.get('/projects');
    return response.data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    return {
      success: false,
      projects: [],
      message: error.response?.data?.message || error.message || 'Failed to fetch projects'
    };
  }
};

export const getPhaseProgress = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/phase-progress`);
    return response.data;
  } catch (error) {
    return { success: false, progress: {}, subphase_progress: {} };
  }
};

export const getProject = async (projectId) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project:', error);
    return {
      success: false,
      project: null,
      message: error.response?.data?.message || error.message || 'Failed to fetch project'
    };
  }
};

export const createProject = async (projectData) => {
  try {
    // Use the correct path without duplicating /api/
    // Check if projectData is FormData (has files) or a standard JSON object
    let response;
    if (projectData instanceof FormData) {
      // For multipart/form-data with file uploads
      response = await axios.post(BACKEND_URL + '/projects', projectData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
        timeout: 300000, // 5 minutes for file uploads
        maxContentLength: 50 * 1024 * 1024, // 50MB max content length
        maxBodyLength: 50 * 1024 * 1024,    // 50MB max body length
      });
    } else {
      // For standard JSON requests
      response = await apiClient.post('/projects', projectData);
    }
    return response.data;
  } catch (error) {
    console.error('Error creating project:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to create project'
    };
  }
};

export const updateProject = async (projectId, projectData) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.put(`/projects/${projectId}`, projectData);
    return response.data;
  } catch (error) {
    console.error('Error updating project:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to update project'
    };
  }
};

export const deleteProject = async (projectId) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.delete(`/projects/${projectId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting project:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to delete project'
    };
  }
};

// Customer management APIs
export const getCustomers = async () => {
  try {
    const response = await apiClient.get('/customers');
    return response.data;
  } catch (error) {
    console.error('Error fetching customers:', error);
    return { success: false, customers: [], message: error.response?.data?.message || 'Failed to fetch customers' };
  }
};

export const createCustomer = async (customerData) => {
  try {
    const response = await apiClient.post('/customers', customerData);
    return response.data;
  } catch (error) {
    console.error('Error creating customer:', error);
    return { success: false, message: error.response?.data?.message || 'Failed to create customer' };
  }
};

export const updateCustomer = async (customerId, customerData) => {
  try {
    const response = await apiClient.put(`/customers/${customerId}`, customerData);
    return response.data;
  } catch (error) {
    console.error('Error updating customer:', error);
    return { success: false, message: error.response?.data?.message || 'Failed to update customer' };
  }
};

export const uploadProjectDocument = async (file, projectId, documentType) => {
  try {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('document_type', documentType === 'SOW' ? 'SOW' : 
                                     documentType === 'SPEC' ? 'functional_spec' : 
                                     documentType === 'REQ' ? 'requirements' : 'project_document');
    
    // Use the correct path without duplicating /api/
    const response = await axios.post(`${BACKEND_URL}/projects/${projectId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      withCredentials: true,
      timeout: 300000, // 5 minutes for large file uploads
      maxContentLength: 50 * 1024 * 1024, // 50MB max content length
      maxBodyLength: 50 * 1024 * 1024,    // 50MB max body length
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading document:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to upload document'
    };
  }
};

export const downloadDocument = async (documentContent, format, companyInfo, documentId = null) => {
  try {
    // Use the correct path without duplicating /api/
    const endpoint = format === 'pdf' ? '/convert-to-pdf' : '/convert-to-docx';
    
    // Extract document ID from markdown content if available (for newly created documents)
    let extractedDocumentId = null;
    let extractedAuthor = null;
    
    // Try to extract document ID from markdown content using regex if we're not given an ID
    if (!documentId && format !== 'md') {
      // Look for Document Information section with ID
      const docInfoPattern = /# Document Information.*?\| Document ID \| ([A-Za-z0-9-]+) \|.*?\| Author \| ([^|]+) \|/s;
      const match = documentContent.match(docInfoPattern);
      
      if (match && match.length >= 3) {
        extractedDocumentId = match[1].trim();
        extractedAuthor = match[2].trim();
        console.log(`Extracted document ID from content: ${extractedDocumentId}, Author: ${extractedAuthor}`);
        
        // Validate document ID format (e.g., FS-YYYYMMDD-XXXX)
        const idPattern = /^FS-\d{8}-\d{4}$/;
        if (!idPattern.test(extractedDocumentId)) {
          console.log(`Document ID ${extractedDocumentId} does not match expected format, will ignore`);
          extractedDocumentId = null;
        }
      }
    }
    
    // Use provided document ID or get from session storage if available, or use extracted ID
    const docId = documentId || extractedDocumentId || sessionStorage.getItem('saved_document_id');
    
    // Extract base document ID if provided
    let baseDocumentId = null;
    if (docId && docId.includes('-')) {
      const parts = docId.split('-');
      if (parts.length >= 3) {
        // Use FS-YYYYMMDD-XXXX format as the base
        baseDocumentId = `${parts[0]}-${parts[1]}-${parts[2]}`;
      }
    }
    
    console.log(`Downloading ${format} with document ID: ${docId}, Base ID: ${baseDocumentId || 'N/A'}`);
    
    // Get project ID from session storage if available
    const projectId = sessionStorage.getItem('projectId');
    if (projectId) {
      console.log(`Including project ID: ${projectId} in document metadata`);
    }
    
    // Include extracted author in metadata if available
    let updatedCompanyInfo = {...(companyInfo || {})};
    if (extractedAuthor && extractedAuthor !== '[Author Name]') {
      updatedCompanyInfo.author = extractedAuthor;
      console.log(`Using author from document: ${extractedAuthor}`);
    }
    
    try {
      const response = await apiClient.post(
        endpoint,
        {
          markdown: documentContent,
          company_info: {
            ...updatedCompanyInfo,
            project_id: projectId || updatedCompanyInfo?.project_id // Include project ID in company_info
          },
          save_to_gcs: true,
          document_name: updatedCompanyInfo?.name ? `${updatedCompanyInfo.name} - SAP Functional Specification` : null,
          document_id: docId, // Pass document ID to link all formats together
          base_document_id: baseDocumentId, // Pass base document ID for consistent versioning
          project_id: projectId // Also include at top level for backward compatibility
        },
        { 
          responseType: 'blob',
          headers: {
            'X-Document-ID': docId || '',
            'X-Base-Document-ID': baseDocumentId || ''
          }
        }
      );
      
      // Check if response is actually an error (sometimes errors come as JSON)
      const contentType = response.headers?.['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // This might be an error response - try to parse it
        const blob = response.data;
        const text = await blob.text();
        
        try {
          const errorJson = JSON.parse(text);
          if (errorJson.error) {
            console.error(`Server error: ${errorJson.error}`);
            return false;
          }
        } catch (jsonError) {
          // Not valid JSON, continue as normal
        }
      }
      
      // Try to get document ID from response headers
      const responseDocId = response.headers?.['x-document-id'];
      if (responseDocId && !docId) {
        sessionStorage.setItem('saved_document_id', responseDocId);
        console.log(`Received new document ID from server: ${responseDocId}`);
      }
      
      // If we're being called from the save function, don't trigger download
      if (!documentId) {
        // Create a download link for the blob
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
        
        // Generate a more descriptive filename
        const projectName = companyInfo?.name ? encodeURIComponent(companyInfo.name.replace(/\s+/g, '_')) : '';
        const timestamp = new Date().toISOString().replace(/[-:]/g, '').substring(0, 15);
        
        // Use document ID in filename if available
        const docIdPart = docId ? `_${docId}` : '';
        const fileName = projectName
          ? `SAP_Spec_${projectName}${docIdPart}.${format}`
          : `SAP_Functional_Spec_${timestamp}${docIdPart}.${format}`;
        
        // Create temporary link element to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        
        // Use the download attribute and click programmatically 
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);
      }
      
      return true;
    } catch (error) {
      console.error(`Error in API call for ${format} document:`, error);
      
      // Check if we have a response with error details
      if (error.response) {
        // Try to extract error message from response
        if (error.response.data) {
          try {
            // If response is a blob, convert to text
            if (error.response.data instanceof Blob) {
              const text = await error.response.data.text();
              try {
                const errorJson = JSON.parse(text);
                console.error('Server error details:', errorJson);
                return false;
              } catch (e) {
                console.error('Error response (text):', text);
                return false;
              }
            } else {
              console.error('Error response data:', error.response.data);
              return false;
            }
          } catch (dataError) {
            console.error('Error parsing error response:', dataError);
            return false;
          }
        }
      }
      
      return false;
    }
  } catch (error) {
    console.error(`Error downloading ${format} document:`, error);
    if (error.response) {
      console.error('Response status:', error.response.status);
      try {
        console.error('Response data:', error.response.data);
      } catch (e) {
        console.error('Could not log response data');
      }
    }
    return false;
  }
};

export const generateSpecification = async (projectDetails, useEnhanced = true) => {
  try {
    // Choose between enhanced (SOW-based) and standard generation
    const apiEndpoint = useEnhanced ? '/generate-spec-enhanced' : '/generate-spec';
      
    console.log('Making request to:', BACKEND_URL + apiEndpoint);
    console.log('Project details:', projectDetails);
    console.log('Using enhanced generation:', useEnhanced);
    
    // Use XMLHttpRequest for more detailed error information
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', BACKEND_URL + apiEndpoint, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (e) {
            console.error('Error parsing response:', e);
            resolve({
              success: false,
              error: 'Invalid response format from server'
            });
          }
        } else {
          console.error('Server error:', xhr.status, xhr.statusText, xhr.responseText);
          resolve({
            success: false,
            error: `Server error: ${xhr.status} ${xhr.statusText}`
          });
        }
      };
      
      xhr.onerror = function() {
        console.error('Network error occurred');
        console.log('Response:', xhr.responseText);
        resolve({
          success: false,
          error: 'Network error occurred. Please ensure the backend server is running.'
        });
      };
      
      xhr.send(JSON.stringify(projectDetails));
    });
  } catch (error) {
    console.error('Error generating specification:', error);
    // Return detailed error information
    return {
      success: false,
      error: error.message || 'Unknown error occurred'
    };
  }
};

export const generateSpecificationEnhanced = async (projectDetails) => {
  return await generateSpecification(projectDetails, true);
};

export const generateSpecificationStandard = async (projectDetails) => {
  return await generateSpecification(projectDetails, false);
};

// Template APIs
export const getAllTemplates = async () => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.get('/templates');
    return response.data;
  } catch (error) {
    console.error('Error fetching templates:', error);
    return [];
  }
};

export const getTemplateById = async (templateId) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.get(`/templates/${templateId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching template:', error);
    return null;
  }
};

export const uploadTemplate = async (formData) => {
  try {
    // Use the correct path without duplicating /api/
    // Use the apiClient which already has the baseURL configured
    const response = await axios.post('/templates', formData, {
      baseURL: BACKEND_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading template:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to upload template'
    };
  }
};

export const generateWithTemplate = async (templateId, aiContent) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/generate-with-template', {
      template_id: templateId,
      ai_content: aiContent,
    });
    return response.data;
  } catch (error) {
    console.error('Error generating with template:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to apply template'
    };
  }
};

export const uploadImage = async (imageFile) => {
  try {
    // Use the correct path without duplicating /api/
    const formData = new FormData();
    formData.append('image', imageFile);
    
    const response = await axios.post('/upload-image', formData, {
      baseURL: BACKEND_URL,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    
    return response.data;
  } catch (error) {
    console.error('Error uploading image:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to upload image'
    };
  }
};

export const enhanceSectionWithAI = async (sectionData) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/ai-enhance-section', sectionData);
    return response.data;
  } catch (error) {
    console.error('Error enhancing section with AI:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to enhance section with AI'
    };
  }
};

// Enhanced section generation with AI for technical specifications
export const enhanceTechnicalSectionWithAI = async (projectId, sectionTitle, sectionContent, enhancementType, customPrompt = '') => {
  try {
    const response = await apiClient.post('/ai/ai-enhance-technical-section', {
      project_id: projectId,
      section_title: sectionTitle,
      section_content: sectionContent,
      enhancement_type: enhancementType,
      custom_prompt: customPrompt
    });
    return response.data;
  } catch (error) {
    console.error('Error enhancing technical section with AI:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to enhance technical section with AI'
    };
  }
};

// New Document Management APIs

export const listDocuments = async (docType, limit = 100, offset = 0) => {
  try {
    // Use the correct path without duplicating /api/
    let url = '/list-documents';
    const params = new URLSearchParams();
    
    if (docType) params.append('type', docType);
    if (limit) params.append('limit', limit);
    if (offset) params.append('offset', offset);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error listing documents:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to list documents'
    };
  }
};

export const getDocument = async (documentId, version = null, download = false) => {
  try {
    // Use the correct path without duplicating /api/
    let url = `/get-document/${documentId}`;
    const params = new URLSearchParams();
    
    if (version) params.append('version', version);
    if (download) params.append('download', 'true');
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error getting document:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get document'
    };
  }
};

export const getDocumentContent = async (documentId, version = null) => {
  try {
    console.log(`Fetching document content for ${documentId}, version=${version}`);
    
    // Use the correct path without duplicating /api/
    let url = `/document-content/${documentId}`;
    const params = new URLSearchParams();
    
    if (version) {
      params.append('version', version);
    }
    // Always request markdown format
    params.append('format', 'md');
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    console.log(`Request URL: ${url}`);
    
    const response = await apiClient.get(url, { 
      responseType: 'text',
      headers: {
        'Accept': 'text/markdown, text/plain'
      }
    });
    
    console.log(`Received response type: ${typeof response.data}`);
    
    if (typeof response.data === 'string') {
      // Check if this looks like a binary file (starts with PK or other non-text markers)
      if (response.data.startsWith('PK') || 
          response.data.includes('\u0000') ||
          /^\uFEFF/.test(response.data)) {
        console.warn('Document appears to be in binary format');
        return {
          success: false,
          error: 'Document is in binary format and cannot be displayed as text',
          content: '# Binary Document\n\nThis document is in a binary format and cannot be displayed. Please download the original document.'
        };
      }
      
      return {
        success: true,
        content: response.data
      };
    } else {
      console.warn('Unexpected response format', response.data);
      return {
        success: false,
        error: 'Unexpected response format',
        content: '# Error\n\nUnexpected document format received.'
      };
    }
  } catch (error) {
    console.error(`Error fetching document content: ${error.message}`);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      content: `# Error\n\nFailed to load document: ${error.message}`
    };
  }
};

export const downloadDocumentById = async (documentId, version = null, format = null) => {
  try {
    if (!documentId) {
      console.error('Error downloading document: No document ID provided');
      return false;
    }
    
    // Create a direct URL to the document download endpoint
    // Use the correct path without duplicating /api/
    let url = `${BACKEND_URL}/get-document/${documentId}?download=true`;
    if (version) {
      url += `&version=${version}`;
      console.log(`Downloading specific version: ${version} of document: ${documentId}`);
    } else {
      console.log(`Downloading latest version of document: ${documentId}`);
    }
    
    // Add format parameter if specified
    if (format) {
      url += `&format=${format}`;
      console.log(`Requesting specific format: ${format} for document: ${documentId}`);
    }
    
    // Show download progress notification
    console.log(`Starting download from URL: ${url}`);
    
    try {
      // Create a popup notification that download is in progress
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/octet-stream'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        // If format-specific download fails, try to get the markdown and convert it
        if (format && (format === 'docx' || format === 'pdf') && response.status === 404) {
          console.log(`Format-specific document not found, attempting on-demand conversion...`);
          return await convertAndDownloadDocument(documentId, format, version);
        }
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }
      
      // Get the filename from the Content-Disposition header if available
      let filename = 'document';
      const contentDisposition = response.headers.get('Content-Disposition');
      console.log('Content-Disposition:', contentDisposition);
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      } 
      
      if (filename === 'document') {
        // If no filename was provided, create a descriptive one
        // Extract document type from ID if possible (FS-YYYYMMDD-XXXX)
        let docTypePrefix = 'SAP_Spec';
        if (documentId.startsWith('FS-')) {
          docTypePrefix = 'SAP_Functional_Spec';
        }
        
        // Create a timestamp for the filename
        const timestamp = new Date().toISOString()
          .replace(/[-:]/g, '')
          .replace(/\.\d+/g, '')
          .replace('T', '_')
          .substring(0, 15);
          
        filename = `${docTypePrefix}_${documentId}_${timestamp}`;
        
        // Get file extension based on Content-Type
        const contentType = response.headers.get('Content-Type');
        console.log('Content-Type:', contentType);
        if (contentType) {
          if (contentType.includes('pdf')) {
            filename += '.pdf';
          } else if (contentType.includes('docx') || contentType.includes('word')) {
            filename += '.docx';
          } else if (contentType.includes('markdown')) {
            filename += '.md';
          }
        }
      }
      
      console.log('Downloading file:', filename);
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.target = '_blank';
      
      // Add to DOM, click, and then clean up
      document.body.appendChild(link);
      link.click();
      
      // Clean up after download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      console.log('Download initiated successfully');
      return true;
    } catch (fetchError) {
      console.error('Fetch error:', fetchError);
      
      // Fallback method: direct window open
      window.open(url, '_blank');
      console.log('Fallback download method used');
      return true;
    }
  } catch (error) {
    console.error('Error downloading document:', error);
    console.error('Error details:', error.message);
    return false;
  }
};

export const getDocumentVersions = async (documentId) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.get(`/document-versions/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting document versions:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get document versions'
    };
  }
};

export const deleteDocument = async (documentId, deleteAllVersions = false) => {
  try {
    // Use the correct path without duplicating /api/
    let url = `/delete-document/${documentId}`;
    if (deleteAllVersions) {
      url += '?all_versions=true';
    }
    
    const response = await apiClient.delete(url);
    return response.data;
  } catch (error) {
    console.error('Error deleting document:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to delete document'
    };
  }
};

export const deleteProjectDocument = async (projectId, documentId) => {
  try {
    const response = await apiClient.delete(`/projects/${projectId}/documents/${documentId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting project document:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to delete document'
    };
  }
};

export const saveMarkdown = async (markdownContent, documentName, metadata = {}, documentId = null, isNewDocument = false, projectId = null, documentType = 'functional_spec') => {
  try {
    // Extract document ID from markdown content if available (for newly created documents)
    let extractedDocumentId = null;
    let extractedAuthor = null;
    
    // Try to extract document ID from markdown content using regex
    if (!documentId || isNewDocument) {
      // Look for Document Information section with ID
      const docInfoPattern = /# Document Information.*?\| Document ID \| ([A-Za-z0-9-]+) \|.*?\| Author \| ([^|]+) \|/s;
      const match = markdownContent.match(docInfoPattern);
      
      if (match && match.length >= 3) {
        extractedDocumentId = match[1].trim();
        extractedAuthor = match[2].trim();
        console.log(`Extracted document ID from content: ${extractedDocumentId}, Author: ${extractedAuthor}`);
        
        // For functional specs, ignore DOC-* IDs from AI generation and let backend generate proper FS-* ID
        if (documentType === 'functional_spec' && extractedDocumentId.startsWith('DOC-')) {
          console.log(`Ignoring DOC-* ID for functional spec, letting backend generate proper FS-* ID`);
          extractedDocumentId = null;
        } else {
          // Validate document ID format (multiple patterns supported)
          const idPatterns = [
            /^FS-\d{8}-\d{4}$/,     // Functional specs
            /^TS-\d{8}-\d{4}$/,     // Technical specs  
            /^SOW-P\d{2}-\d{8}$/,   // SOW documents
            /^REQ-\d{8}-\d{4}$/,    // Requirements
            /^DES-\d{8}-\d{4}$/,    // Design docs
            /^TP-\d{8}-\d{4}$/,     // Test plans
            /^UM-\d{8}-\d{4}$/,     // User manuals
            /^DOC-\d{8}-\d{4}$/     // Other documents
          ];
          
          const isValidFormat = idPatterns.some(pattern => pattern.test(extractedDocumentId));
          if (!isValidFormat) {
            console.log(`Document ID ${extractedDocumentId} does not match any expected format, will generate new ID`);
            extractedDocumentId = null;
          }
        }
        
        // Update metadata with extracted author if not placeholder
        if (extractedAuthor && extractedAuthor !== '[Author Name]') {
          metadata.author = extractedAuthor;
        }
      }
    }
    
    // Use extracted ID only if no explicit ID was provided
    const finalDocumentId = documentId || extractedDocumentId;
    
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/save-markdown', {
      markdown: markdownContent,
      document_name: documentName,
      metadata: metadata,
      document_id: finalDocumentId,  // Pass document ID to prevent duplication
      is_new_document: isNewDocument,  // Flag to indicate if this is a new document and should get a new ID
      project_id: projectId || sessionStorage.getItem('projectId'), // Get project ID from param or session storage
      document_type: documentType  // Include document type
    });
    
    return response.data;
  } catch (error) {
    console.error('Error saving markdown:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to save markdown'
    };
  }
};

export const compareVersions = async (documentId, version1, version2) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/compare-versions', {
      document_id: documentId,
      version1: version1,
      version2: version2
    });
    
    return response.data;
  } catch (error) {
    console.error('Error comparing versions:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to compare versions'
    };
  }
};

export const analyzeDocuments = async (analysisType, documentIds, options = {}) => {
  try {
    // Use the correct path without duplicating /api/
    const response = await apiClient.post('/ai-document-analysis', {
      analysis_type: analysisType,
      document_ids: documentIds,
      options: options
    });
    
    return response.data;
  } catch (error) {
    console.error('Error analyzing documents:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to analyze documents'
    };
  }
};

// Document type management APIs
export const getDocumentsByType = async (documentType, projectId = null, latestOnly = true) => {
  try {
    let url = '/documents-by-type';
    const params = new URLSearchParams();
    
    if (documentType) params.append('type', documentType);
    if (projectId) params.append('project_id', projectId);
    if (latestOnly !== undefined) params.append('latest_only', latestOnly.toString());
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error getting documents by type:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get documents by type',
      documents: []
    };
  }
};

export const getDocumentTypes = async () => {
  try {
    const response = await apiClient.get('/document-types');
    return response.data;
  } catch (error) {
    console.error('Error getting document types:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get document types',
      document_types: []
    };
  }
};

// Helper function to convert and download a document when specific format is not available
const convertAndDownloadDocument = async (documentId, format, version = null) => {
  try {
    // First, get the markdown content
    const contentResponse = await getDocumentContent(documentId, version);
    
    if (!contentResponse.success) {
      console.error('Failed to get document content for conversion:', contentResponse.error);
      return false;
    }
    
    const markdownContent = contentResponse.content;
    
    // Get project information for proper naming
    const projectId = sessionStorage.getItem('projectId');
    let companyInfo = {
      name: 'SAP Project',
      author: 'Generated',
      date: new Date().toISOString().split('T')[0]
    };
    
    if (projectId) {
      companyInfo.project_id = projectId;
    }
    
    // Use the downloadDocument function to convert and download
    console.log(`Converting document ${documentId} to ${format}...`);
    const downloadResult = await downloadDocument(markdownContent, format, companyInfo, documentId);
    
    if (downloadResult && downloadResult.success) {
      console.log(`Successfully converted and downloaded ${documentId} as ${format}`);
      return true;
    } else {
      console.error('Failed to convert and download document:', downloadResult);
      return false;
    }
  } catch (error) {
    console.error('Error in convertAndDownloadDocument:', error);
    return false;
  }
};

export const getDocumentByDocumentId = async (documentId) => {
  try {
    // Get all user documents and find the one with matching document_id
    const response = await apiClient.get('/documents', {
      params: {
        latest_only: true,
        limit: 1000
      }
    });
    
    if (response.data.success && response.data.documents) {
      const document = response.data.documents.find(doc => doc.document_id === documentId);
      if (document) {
        return {
          success: true,
          document: document
        };
      } else {
        return {
          success: false,
          error: 'Document not found'
        };
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to fetch documents'
      };
    }
  } catch (error) {
    console.error('Error getting document by document ID:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to get document'
    };
  }
};

// Project Intelligence Chat
export const projectAiChat = (projectId, message, history = []) =>
  apiClient.post(`/ai/projects/${projectId}/ai-chat`, { message, history });

// Chat Sessions
export const listChatSessions = (projectId) =>
  apiClient.get(`/ai/projects/${projectId}/chat-sessions`);
export const createChatSession = (projectId) =>
  apiClient.post(`/ai/projects/${projectId}/chat-sessions`);
export const getChatSession = (projectId, sessionId) =>
  apiClient.get(`/ai/projects/${projectId}/chat-sessions/${sessionId}`);
export const renameChatSession = (projectId, sessionId, title) =>
  apiClient.patch(`/ai/projects/${projectId}/chat-sessions/${sessionId}`, { title });
export const deleteChatSession = (projectId, sessionId) =>
  apiClient.delete(`/ai/projects/${projectId}/chat-sessions/${sessionId}`);
export const projectAiChatInSession = (projectId, sessionId, message, history = []) =>
  apiClient.post(`/ai/projects/${projectId}/ai-chat`, { message, history, session_id: sessionId });
